import { useState, useMemo } from 'react';
import { getOpenCommands, closeCommand, removeItemFromCommand, deleteCommand, deleteAllOpenCommands } from '@/lib/commands';
import { useAuth } from '@/context/AuthContext';
import { previewCommandReceipt } from '@/lib/printer';
import { printCommandReceipt } from '@/lib/printSmart';
import { formatCurrency, formatDateTime } from '@/lib/formatters';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Banknote, CreditCard, QrCode, Receipt, Printer, FileText, RefreshCw, Ticket, CalendarCheck, Sparkles, Search, X, Trash2, ShieldAlert,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import CoffeeComboButton from '@/components/CoffeeComboButton';
import DailyCloseModal from '@/components/DailyCloseModal';
import UnifiedScanner from '@/components/UnifiedScanner';
import CashDrawerCard from '@/components/CashDrawerCard';

const PAYMENT_METHODS = [
  { value: 'Dinheiro', icon: Banknote, active: 'bg-amber-600 hover:bg-amber-700' },
  { value: 'Pix', icon: QrCode, active: 'bg-emerald-600 hover:bg-emerald-700' },
  { value: 'Crédito', icon: CreditCard, active: 'bg-blue-600 hover:bg-blue-700' },
  { value: 'Débito', icon: CreditCard, active: 'bg-indigo-600 hover:bg-indigo-700' },
  { value: 'Voucher', icon: Ticket, active: 'bg-fuchsia-600 hover:bg-fuchsia-700' },
];

// Parser tolerante: aceita "15,50", "15.50", "R$ 15,50"
function parseAmountBR(raw) {
  if (raw === '' || raw === null || raw === undefined) return NaN;
  const cleaned = String(raw)
    .replace(/[^\d,\.]/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '') // remove ponto de milhar
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

export default function CaixaPage() {
  const { operator } = useAuth();
  const [commands, setCommands] = useState([]);
  const [selected, setSelected] = useState(null);
  const [closing, setClosing] = useState(false);
  const [chosenMethod, setChosenMethod] = useState(null);
  const [amountStr, setAmountStr] = useState('');
  const [serviceOn, setServiceOn] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showDailyClose, setShowDailyClose] = useState(false);
  const [search, setSearch] = useState('');
  // Fase 30 — Exclusão admin
  const [deleteTarget, setDeleteTarget] = useState(null); // comanda a excluir (unitária)
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const isAdmin = operator?.role === 'admin';

  const load = async () => {
    const list = await getOpenCommands();
    setCommands(list);
    // Refresca a comanda selecionada apenas se o ID ainda existe — evita
    // setState desnecessário em loop quando nada mudou.
    setSelected((prev) => {
      if (!prev) return prev;
      const fresh = list.find((c) => c.id === prev.id);
      // Só troca a referência se o updatedAt mudou (ou se sumiu)
      if (!fresh) return null;
      if (fresh.updatedAt === prev.updatedAt && fresh.total === prev.total) return prev;
      return fresh;
    });
  };

  // Fase 27 — polling com visibility guard (pausa em background).
  useVisiblePolling(load, 8000);

  // Fase 23 — Filtro de busca por número de comanda.
  const filteredCommands = useMemo(() => {
    const q = search.trim();
    if (!q) return commands;
    return commands.filter((c) => String(c.number).includes(q));
  }, [commands, search]);

  const handleRemoveItem = async (itemIndex, it) => {    if (!selected) return;
    if (!window.confirm(`Remover ${it.quantity}× ${it.name} da comanda #${selected.number}?\n\nEsta ação será registrada no Log de Auditoria.`)) return;
    try {
      const updated = await removeItemFromCommand(selected.id, itemIndex, operator?.id);
      setSelected(updated);
      load();
      toast.success(`Removido: ${it.quantity}× ${it.name}`);
    } catch (err) {
      toast.error(err.message || 'Falha ao remover item');
    }
  };

  // Fase 30 — Exclusão admin
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await deleteCommand(deleteTarget.id, operator?.id);
      toast.success(`Comanda #${r.number} excluída`, {
        description: 'Itens devolvidos ao estoque. Registrado no Log de Auditoria.',
      });
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err.message || 'Falha ao excluir comanda');
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (bulkConfirmText.trim().toUpperCase() !== 'CONFIRMAR') {
      toast.error('Digite CONFIRMAR para prosseguir');
      return;
    }
    try {
      const r = await deleteAllOpenCommands(operator?.id);
      if (r.deleted === 0) {
        toast.info('Não havia comandas abertas');
      } else {
        toast.success(`${r.deleted} comanda(s) aberta(s) excluída(s)`, {
          description: `Total cancelado: ${formatCurrency(r.totalValue)}. Estoque devolvido.`,
        });
      }
      setShowBulkDelete(false);
      setBulkConfirmText('');
      load();
    } catch (err) {
      toast.error(err.message || 'Falha na exclusão em massa');
    }
  };

  const baseTotal = Number(selected?.total || 0);
  const serviceCharge = serviceOn ? Math.round(baseTotal * 10) / 100 : 0; // 10% arredondado
  const finalTotal = Math.round((baseTotal + serviceCharge) * 100) / 100;
  const received = parseAmountBR(amountStr);
  const isCash = chosenMethod === 'Dinheiro';
  const receivedValid = !isCash || (Number.isFinite(received) && received >= finalTotal);
  const change = isCash && Number.isFinite(received) ? Math.max(received - finalTotal, 0) : 0;

  const canConfirm = useMemo(() => {
    if (!chosenMethod) return false;
    if (isCash) return receivedValid;
    return true;
  }, [chosenMethod, isCash, receivedValid]);

  const openClose = (c) => {
    setSelected(c);
    setChosenMethod(null);
    setAmountStr('');
    setServiceOn(false);
  };

  const closeDialog = () => {
    setSelected(null);
    setChosenMethod(null);
    setAmountStr('');
    setServiceOn(false);
  };

  const handleConfirmClose = async () => {
    if (!selected || !chosenMethod) {
      toast.error('Selecione o método de pagamento');
      return;
    }
    if (isCash && !receivedValid) {
      toast.error('Valor recebido insuficiente');
      return;
    }
    setClosing(true);
    try {
      const details = {
        serviceCharge,
        finalTotal,
      };
      if (isCash) {
        details.amountReceived = received;
        details.changeGiven = change;
      }
      const paid = await closeCommand(selected.id, chosenMethod, details, operator);
      const msg = isCash && change > 0
        ? `Comanda #${paid.number} paga — Troco ${formatCurrency(change)}`
        : `Comanda #${paid.number} paga em ${chosenMethod}`;
      toast.success(msg);
      closeDialog();
      load();
    } finally {
      setClosing(false);
    }
  };

  const handlePreview = (c) => {
    const text = previewCommandReceipt(c);
    setPreview({ command: c, text });
  };

  const handlePrint = async (c) => {
    try {
      const r = await printCommandReceipt(c);
      toast.success('Extrato enviado para impressão', {
        description: r.mode === 'windows' ? 'Confira o diálogo do sistema' : 'Via Jetway (ESC/POS)',
      });
    } catch (err) {
      toast.error(err.message || 'Falha ao imprimir');
    }
  };

  return (
    <div data-testid="caixa-page" className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-3xl tracking-tight font-bold text-slate-900">Caixa</h2>
          <p className="text-sm text-slate-500 mt-1">Fechamento de comandas e pagamentos</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="caixa-refresh-btn"
            onClick={load}
            className="flex items-center gap-2 px-4 h-10 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
          {isAdmin && commands.length > 0 && (
            <button
              data-testid="caixa-bulk-delete-btn"
              onClick={() => setShowBulkDelete(true)}
              title="Excluir todas as comandas abertas (admin)"
              className="flex items-center gap-2 px-4 h-10 rounded-lg border-2 border-rose-200 bg-white text-sm font-bold text-rose-600 hover:bg-rose-50 hover:border-rose-400 transition-colors"
            >
              <ShieldAlert size={15} strokeWidth={2} /> Excluir todas
            </button>
          )}
          <button
            data-testid="caixa-close-day-btn"
            onClick={() => setShowDailyClose(true)}
            className="flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
          >
            <CalendarCheck size={15} strokeWidth={2} /> Encerrar Dia
          </button>
        </div>
      </div>

      <div className="mb-6">
        <CoffeeComboButton variant="compact" onDone={load} />
      </div>

      {/* Fase 31 — Sessão de caixa: abertura, sangria/suprimento, fechamento */}
      <CashDrawerCard onChange={load} />

      {/* Fase 32 — Scanner unificado: cartão (1-4 dig) ou etiqueta (EAN-13) */}
      <UnifiedScanner operator={operator} onSaleCompleted={load} />

      {/* Fase 23 — Busca por número de comanda */}
      <div className="mb-4 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          data-testid="caixa-search-input"
          type="search"
          inputMode="numeric"
          placeholder="Buscar por número da comanda..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-11 pl-10 pr-10 border-2 border-slate-200 bg-white rounded-xl text-sm font-semibold"
        />
        {search && (
          <button
            data-testid="caixa-search-clear"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center"
            title="Limpar busca"
          >
            <X size={14} />
          </button>
        )}
        {search && (
          <p className="text-[11px] text-slate-500 mt-1.5 ml-1">
            {filteredCommands.length} resultado{filteredCommands.length === 1 ? '' : 's'} para "#{search}"
          </p>
        )}
      </div>

      {filteredCommands.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center">
          <Receipt size={48} className="mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="font-bold text-slate-700">{search ? 'Nenhuma comanda bate com a busca' : 'Nenhuma comanda aberta'}</p>
          <p className="text-sm text-slate-500 mt-1">
            {search ? 'Tente outro número ou limpe a busca.' : 'Comandas aparecerão aqui quando um cliente for pesado na balança.'}
          </p>
        </div>
      ) : (
        <div data-testid="caixa-grid" translate="no" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredCommands.map((c) => (
            <div key={c.id} data-testid={`caixa-cmd-${c.number}`} translate="no" className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Comanda</p>
                  <p className="font-mono text-4xl font-black text-slate-900 tracking-tighter">#{c.number}</p>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Aberta</Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Buffet</p>
                  <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(c.buffetTotal || 0)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Bebidas</p>
                  <p className="font-mono text-sm font-bold text-slate-900">{formatCurrency(c.itemsTotal || 0)}</p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-3 mb-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total</p>
                <p data-testid={`caixa-total-${c.number}`} className="font-mono text-2xl font-black text-slate-900 tracking-tighter">
                  {formatCurrency(c.total || 0)}
                </p>
                <p className="text-[10px] text-slate-400 mt-1">Aberta {formatDateTime(c.createdAt)}</p>
              </div>

              <div className="flex gap-2">
                <button
                  data-testid={`caixa-preview-${c.number}`}
                  onClick={() => handlePreview(c)}
                  className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:translate-y-[1px] transition-all shadow-sm"
                  title="Preview do extrato"
                >
                  <FileText size={13} strokeWidth={2.25} /> Preview
                </button>
                <button
                  data-testid={`caixa-print-${c.number}`}
                  onClick={() => handlePrint(c)}
                  className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 active:translate-y-[1px] transition-all shadow-sm"
                  title="Imprimir cupom"
                >
                  <Printer size={13} strokeWidth={2.25} /> Imprimir
                </button>
                <button
                  data-testid={`caixa-close-${c.number}`}
                  onClick={() => openClose(c)}
                  className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 active:translate-y-[1px] transition-all shadow-sm"
                  title="Fechar comanda e cobrar"
                >
                  Fechar
                </button>
                {isAdmin && (
                  <button
                    data-testid={`caixa-delete-${c.number}`}
                    onClick={() => setDeleteTarget(c)}
                    title="Excluir comanda (admin)"
                    className="shrink-0 w-10 h-10 rounded-lg border border-rose-200 bg-white text-rose-500 hover:bg-rose-50 hover:border-rose-400 flex items-center justify-center transition-colors"
                  >
                    <Trash2 size={14} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Close Dialog - Multimodal + Troco */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent data-testid="caixa-close-dialog" className="max-w-md bg-white border border-slate-200 rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Fechar Comanda #{selected?.number}</DialogTitle>
            <DialogDescription>Revise os itens, selecione o método e confirme.</DialogDescription>
          </DialogHeader>

          {/* Fase 23 — Lista de itens com opção de remover (auditoria) */}
          {selected?.items?.length > 0 && (
            <div data-testid="caixa-items-list" className="bg-white border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Itens ({selected.items.length})
                </p>
                <p className="text-[10px] text-slate-400">X remove (registra auditoria)</p>
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                {selected.items.map((it, idx) => (
                  <div key={`${it.name}-${it.quantity}-${idx}`} data-testid={`caixa-item-row-${idx}`} className="flex items-center gap-2 px-3 py-2">
                    <span className="text-sm text-slate-700 min-w-0 flex-1 truncate">
                      {it.quantity}× {it.name}
                    </span>
                    <span className="font-mono text-xs font-bold text-slate-900 shrink-0">{formatCurrency(it.subtotal)}</span>
                    <button
                      data-testid={`caixa-remove-item-${idx}`}
                      onClick={() => handleRemoveItem(idx, it)}
                      className="shrink-0 w-7 h-7 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700 border border-transparent hover:border-rose-200 flex items-center justify-center transition-all"
                      title="Remover item"
                    >
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-slate-50 rounded-xl p-4 my-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                  {serviceOn ? 'Total com Serviço' : 'Total a receber'}
                </p>
                <p
                  data-testid="dialog-total"
                  className="font-mono text-3xl font-black text-slate-900 tracking-tighter"
                >
                  {formatCurrency(finalTotal)}
                </p>
                {serviceOn && (
                  <p data-testid="dialog-service-line" className="text-[11px] text-emerald-700 font-semibold mt-1">
                    <span className="font-mono">{formatCurrency(baseTotal)}</span>
                    <span className="text-slate-400 mx-1">+</span>
                    <span className="font-mono">{formatCurrency(serviceCharge)}</span>
                    <span className="text-slate-400 ml-1">(10% serviço)</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Toggle 10% Serviço */}
          <label
            htmlFor="service-toggle"
            className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${
              serviceOn
                ? 'border-emerald-400 bg-emerald-50/70'
                : 'border-slate-200 bg-white hover:border-slate-400'
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${serviceOn ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                <Sparkles size={17} strokeWidth={1.75} />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-bold ${serviceOn ? 'text-emerald-800' : 'text-slate-800'}`}>
                  Adicionar 10% de Serviço
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                  {serviceOn
                    ? `+ ${formatCurrency(serviceCharge)} de gorjeta`
                    : 'Gorjeta para a equipe'}
                </p>
              </div>
            </div>
            <Switch
              id="service-toggle"
              data-testid="service-toggle"
              checked={serviceOn}
              onCheckedChange={setServiceOn}
            />
          </label>

          {/* 5 métodos em grid 5 colunas (compacto) */}
          <div className="grid grid-cols-5 gap-2 mt-3">
            {PAYMENT_METHODS.map((m) => {
              const Icon = m.icon;
              const isActive = chosenMethod === m.value;
              return (
                <button
                  key={m.value}
                  data-testid={`payment-${m.value.toLowerCase()}`}
                  onClick={() => {
                    setChosenMethod(m.value);
                    if (m.value !== 'Dinheiro') setAmountStr('');
                  }}
                  className={`h-20 rounded-xl border-2 flex flex-col items-center justify-center gap-1 font-bold text-[10px] transition-all ${
                    isActive
                      ? 'text-white border-transparent ' + m.active
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-900'
                  }`}
                >
                  <Icon size={20} strokeWidth={1.75} />
                  {m.value}
                </button>
              );
            })}
          </div>

          {/* Calculadora de Troco (só Dinheiro) */}
          {isCash && (
            <div data-testid="cash-panel" className="mt-4 bg-amber-50/60 border border-amber-200 rounded-xl p-4 space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1.5 block">
                  Valor Recebido
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">R$</span>
                  <input
                    data-testid="cash-received-input"
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                    placeholder="0,00"
                    className="w-full h-12 pl-10 pr-3 rounded-lg border-2 border-amber-200 bg-white font-mono text-xl font-bold text-slate-900 focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div className="bg-white border-2 border-dashed border-emerald-300 rounded-lg p-3 text-center">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700/80">
                  Troco
                </p>
                {Number.isFinite(received) && received >= finalTotal ? (
                  <p
                    data-testid="cash-change-display"
                    className="font-mono text-3xl font-black text-emerald-600 tracking-tighter mt-1"
                  >
                    {formatCurrency(change)}
                  </p>
                ) : Number.isFinite(received) && received < finalTotal ? (
                  <p
                    data-testid="cash-insufficient"
                    className="font-mono text-base font-bold text-red-500 mt-1"
                  >
                    Faltam {formatCurrency(finalTotal - received)}
                  </p>
                ) : (
                  <p className="font-mono text-3xl font-black text-slate-300 tracking-tighter mt-1">
                    R$ 0,00
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <button onClick={closeDialog} className="px-5 h-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button
              data-testid="confirm-payment-btn"
              onClick={handleConfirmClose}
              disabled={!canConfirm || closing}
              className="px-5 h-10 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              {closing ? 'Fechando...' : 'Finalizar e Cobrar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-md bg-white border border-slate-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Preview Extrato</DialogTitle>
          </DialogHeader>
          <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed max-h-96">
            {preview?.text}
          </pre>
        </DialogContent>
      </Dialog>

      {/* Daily Close Flash Modal */}
      <DailyCloseModal open={showDailyClose} onOpenChange={setShowDailyClose} />

      {/* Fase 30 — Dialog: Excluir uma comanda (admin) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent data-testid="caixa-delete-dialog" className="max-w-md bg-white border-2 border-rose-200 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-rose-700 flex items-center gap-2">
              <ShieldAlert size={20} /> Excluir Comanda #{deleteTarget?.number}?
            </DialogTitle>
            <DialogDescription>
              Esta ação <strong>não pode ser desfeita</strong>. A comanda será removida da lista ativa e as bebidas serão devolvidas ao estoque.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-1 text-sm">
              <p className="flex justify-between">
                <span className="text-slate-600">Total atual:</span>
                <strong className="font-mono text-rose-700">{formatCurrency(deleteTarget.total || 0)}</strong>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-600">Itens:</span>
                <strong>{(deleteTarget.items || []).length}</strong>
              </p>
              <p className="flex justify-between">
                <span className="text-slate-600">Pesagens:</span>
                <strong>{(deleteTarget.weightEntries || []).length}</strong>
              </p>
              <p className="text-[11px] text-rose-600 mt-2 pt-2 border-t border-rose-200">
                ⚠️ A ação ficará registrada no <strong>Log de Auditoria</strong> com seu nome.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 mt-2">
            <button
              data-testid="caixa-delete-cancel"
              onClick={() => setDeleteTarget(null)}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              data-testid="caixa-delete-confirm"
              onClick={handleConfirmDelete}
              className="flex-1 h-11 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700"
            >
              Excluir definitivamente
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase 30 — Dialog: Excluir TODAS as comandas abertas (admin) */}
      <Dialog open={showBulkDelete} onOpenChange={(o) => { if (!o) { setShowBulkDelete(false); setBulkConfirmText(''); } }}>
        <DialogContent data-testid="caixa-bulk-delete-dialog" className="max-w-md bg-white border-2 border-rose-300 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-rose-700 flex items-center gap-2">
              <ShieldAlert size={22} /> Excluir TODAS as comandas abertas?
            </DialogTitle>
            <DialogDescription>
              Isso vai cancelar <strong>{commands.length} comanda(s)</strong> aberta(s) de uma vez. Estoque será devolvido e tudo ficará registrado no Log de Auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <p className="text-sm font-bold text-rose-800 mb-1">
              Total que será cancelado: <span className="font-mono">{formatCurrency(commands.reduce((a, c) => a + Number(c.total || 0), 0))}</span>
            </p>
            <p className="text-[11px] text-rose-700 mb-3">Esta ação <strong>não pode ser desfeita</strong>.</p>
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">
              Digite <strong className="text-rose-700">CONFIRMAR</strong> para prosseguir
            </label>
            <Input
              data-testid="caixa-bulk-delete-input"
              value={bulkConfirmText}
              onChange={(e) => setBulkConfirmText(e.target.value)}
              placeholder="CONFIRMAR"
              className="h-11 border-2 border-rose-300 rounded-xl font-mono font-bold text-center uppercase"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 mt-2">
            <button
              data-testid="caixa-bulk-delete-cancel"
              onClick={() => { setShowBulkDelete(false); setBulkConfirmText(''); }}
              className="flex-1 h-11 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              data-testid="caixa-bulk-delete-confirm"
              onClick={handleConfirmBulkDelete}
              disabled={bulkConfirmText.trim().toUpperCase() !== 'CONFIRMAR'}
              className="flex-1 h-11 rounded-xl bg-rose-600 text-white font-bold text-sm hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Excluir {commands.length} comanda(s)
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
